import { format, formatDistanceToNow } from 'date-fns'
import { enUS, fr } from 'date-fns/locale'

// Backend stores all monetary amounts in minor units (centimes/cents = ×100).
// Forms always work with whole-unit values (FCFA, EUR, USD) to match the
// user's mental model. Use these helpers at the form boundary.
export function toMinorUnits(amount: number | string | undefined | null): number {
  if (amount === undefined || amount === null || amount === '') return 0
  const n = typeof amount === 'string' ? Number.parseFloat(amount) : amount
  if (!Number.isFinite(n)) return 0
  return Math.round(n * 100)
}

export function fromMinorUnits(minorUnits: number | undefined | null): number {
  if (minorUnits === undefined || minorUnits === null) return 0
  return minorUnits / 100
}

export function formatXOF(centimes: number): string {
  const units = Math.round(centimes / 100)
  return new Intl.NumberFormat('fr-SN', {
    style: 'currency',
    currency: 'XOF',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(units)
}

export type DisplayCurrencyCode = 'XOF' | 'USD' | 'EUR'

// formatCurrency renders an amount stored in its own native currency's
// minor units (centimes for XOF, cents for USD/EUR). No FX conversion.
// Use this when the row carries a `currency` field that already matches
// the stored amount.
export function formatCurrency(minorUnits: number, currency: DisplayCurrencyCode): string {
  if (currency === 'XOF') return formatXOF(minorUnits)
  const major = minorUnits / 100
  return new Intl.NumberFormat(currency === 'EUR' ? 'fr-FR' : 'en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(major)
}

// formatMoney renders an amount stored as XOF centimes in the requested
// display currency. The backend stays XOF-only — this is a display-side
// conversion using manual rates from preferences.
export function formatMoney(
  centimes: number,
  currency: DisplayCurrencyCode,
  xofPerUsd = 600,
  xofPerEur = 655.957,
): string {
  if (currency === 'USD' || currency === 'EUR') {
    const rate = currency === 'USD' ? xofPerUsd : xofPerEur
    const safeRate = rate > 0 ? rate : 1
    const major = centimes / 100 / safeRate
    return new Intl.NumberFormat(currency === 'EUR' ? 'fr-FR' : 'en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(major)
  }
  return formatXOF(centimes)
}

export function formatNumber(value: number, locale: 'fr' | 'en' = 'fr'): string {
  return new Intl.NumberFormat(locale === 'fr' ? 'fr-SN' : 'en-US').format(value)
}

export function formatDate(date: string | Date, locale: 'fr' | 'en' = 'fr'): string {
  return format(new Date(date), 'dd MMM yyyy', { locale: locale === 'fr' ? fr : enUS })
}

export function formatDateTime(date: string | Date, locale: 'fr' | 'en' = 'fr'): string {
  return format(new Date(date), 'dd MMM yyyy · HH:mm', {
    locale: locale === 'fr' ? fr : enUS,
  })
}

export function formatRelativeDate(date: string | Date, locale: 'fr' | 'en' = 'fr'): string {
  return formatDistanceToNow(new Date(date), {
    addSuffix: true,
    locale: locale === 'fr' ? fr : enUS,
  })
}

export function formatWeight(kg: number): string {
  return kg >= 1000 ? `${(kg / 1000).toFixed(1)} t` : `${kg.toFixed(0)} kg`
}

export function formatVolume(cbm: number): string {
  return `${cbm.toFixed(2)} m³`
}

// formatUnit maps the backend line unit label to a display-friendly
// token. Percent surcharges come back as "percent" — render as "%" so
// rows don't read "1 percent" in the table.
export function formatUnit(unit?: string | null): string {
  switch (unit) {
    case 'percent':
      return '%'
    case 'cbm':
      return 'm³'
    case null:
    case undefined:
    case '':
      return ''
    default:
      return unit
  }
}
